import {Action} from '@reduxjs/toolkit'
import {persistReducer} from 'redux-persist'
import storage from 'redux-persist/lib/storage'
import {EFormJobTimeRule, EFormJobType, EJobPriority} from 'src/app/models'
import {v4 as uuidv4} from 'uuid'

export interface ActionWithPayload<T> extends Action {
  payload?: T
}

export interface IJobState {
  wo_form?: {
    wo_id?: string
    wo_number?: string
    reviewer_name?: string
    reviewer_time?: string
    requestor_department?: string
    attachment?: {
      original_file_name?: string
      size?: number
      path_url?: string
    }
  }
  job_form?: {
    // Informasi tugas
    company_id?: string
    company_label?: string
    department_id?: string
    department_label?: string
    name?: string
    priority?: keyof typeof EJobPriority
    wo_number?: string
    description?: string
    address?: string
    location?: string
    location_name?: string
    job_type?: string

    // informasi jadwal
    time_rule?: keyof typeof EFormJobTimeRule
    start_date?: string
    end_date?: string
    start_time?: string
    end_time?: string
    is_recurring?: boolean
    recurring_interval?: number
    recurring_unit?: {
      label?: string
      value?: string
    }
    recurring_end?: string
    recurring_end_date?: string
    recurring_end_count?: number

    recurring_custom_interval?: number
    recurring_custom_unit?: string
    recurring_custom_days?: string[]
    recurring_customized?: {
      label?: string
      value?: string
    }

    // informasi agen
    pic_agent?: {
      label?: string
      value?: string
    }[]
    has_attendance?: boolean
    radius?: string
    is_selfie?: boolean

    // pemeriksa tugas
    approval?: boolean
    approval_by?: string
    approval_by_label?: string
  }
  forms?: {
    id?: string
    name?: string
    type?: keyof typeof EFormJobType
    description?: string
    is_required?: boolean
    data?: {
      name?: string
      id?: string
    }[]
  }[]
  task_template?: {
    id?: string
    name?: string
  }
  step?: number
  errors?: any
  touched?: any
}

namespace JobRedux {
  export const actionTypes = {
    SetJobForm: '[SetJobForm] Action',
    SetTaskForm: '[SetTaskForm] Action',
    SetTaskTemplateForm: '[SetTaskTemplateForm] Action',
    SetWoForm: '[SetWoForm] Action',
    SetStepForm: '[SetStepForm] Action',
    SetResetForm: '[SetResetForm] Action',
    SetError: '[SetError] Action',
  }

  export const initialJobState: IJobState = {
    wo_form: {
      wo_id: '',
      wo_number: '',
      reviewer_name: '',
      reviewer_time: '',
      requestor_department: '',
      attachment: {
        original_file_name: '',
        size: undefined,
        path_url: '',
      },
    },
    job_form: {
      // Informasi tugas
      company_id: '',
      company_label: '',
      department_id: '',
      department_label: '',
      name: '',
      priority: undefined,
      wo_number: '',
      description: '',
      address: '',
      location: '',
      location_name: '',
      job_type: 'SINGLE',

      // informasi jadwal
      time_rule: 'FIXED',
      start_date: '',
      end_date: '',
      start_time: '',
      end_time: '',
      is_recurring: false,
      recurring_interval: 1,
      recurring_unit: undefined,
      recurring_end: 'DATE' as 'DATE' | 'COUNT',
      recurring_end_date: '',
      recurring_end_count: 0,

      recurring_custom_interval: 1,
      recurring_custom_unit: 'DAY',
      recurring_custom_days: [],
      recurring_customized: undefined,

      // informasi agen
      pic_agent: [],
      has_attendance: false,
      radius: '',
      is_selfie: false,

      // pemeriksa tugas
      approval: false,
      approval_by: '',
      approval_by_label: '',
    },
    forms: [
      {
        id: uuidv4(),
        name: '',
        type: undefined,
        description: '',
        is_required: true,
        data: [
          {
            id: uuidv4(),
            name: '',
          },
        ],
      },
    ],
    task_template: {
      id: '',
      name: '',
    },
    step: 1,
    errors: {},
    touched: {},
  }

  export const reducer = () =>
    persistReducer<IJobState, ActionWithPayload<IJobState>>(
      {
        storage,
        key: 'JOB-FORM',
        whitelist: [''],
      },
      (state: IJobState = initialJobState, action = {type: ''}) => {
        switch (action.type) {
          case actionTypes.SetWoForm: {
            const form = action.payload?.wo_form

            return {...state, wo_form: form}
          }
          case actionTypes.SetJobForm: {
            const form = action.payload?.job_form

            return {...state, job_form: form}
          }

          case actionTypes.SetTaskForm: {
            const form = action.payload?.forms

            return {...state, forms: form}
          }

          case actionTypes.SetTaskTemplateForm: {
            const form = action.payload?.task_template

            return {...state, task_template: form}
          }

          case actionTypes.SetStepForm: {
            const step = action.payload?.step

            return {...state, step}
          }

          case actionTypes.SetError: {
            const errors = action.payload?.errors
            const touched = action.payload?.touched
            return {...state, errors, touched}
          }

          case actionTypes.SetResetForm: {
            return initialJobState
          }

          default:
            return state
        }
      }
    )

  export const actions = {
    setWoForm: (data: IJobState) => ({
      type: actionTypes.SetWoForm,
      payload: data,
    }),
    setJobForm: (data: IJobState) => ({
      type: actionTypes.SetJobForm,
      payload: data,
    }),
    setTaskForm: (data: IJobState) => ({
      type: actionTypes.SetTaskForm,
      payload: data,
    }),
    setTaskTemplateForm: (data: IJobState) => ({
      type: actionTypes.SetTaskTemplateForm,
      payload: data,
    }),
    setStepForm: (step: number) => ({
      type: actionTypes.SetStepForm,
      payload: {step},
    }),
    setResetForm: () => ({
      type: actionTypes.SetResetForm,
    }),
    setErrors: (errors: any, touched: any) => ({
      type: actionTypes.SetError,
      payload: {errors, touched},
    }),
  }
}

export default JobRedux
